import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import NewPageTitleBar from '@/components/shared/NewPageTitleBar';
import FilterShopDateRange from '@/components/filters/FilterShopDateRange';
import AppCard from '@/components/appcard/AppCard';
import AppSectionLoader from '@/components/AppSectionLoader';
import WcnApexchart from '@/components/charts/WcnApexchart';
import { getMenuData } from '@/core/data/menuData';
import { apiClient } from '@core/api';
import { useNotifications } from '@/core/hooks/useNotifications';
import { FCSecurePage, useRegisterRefresh } from '@/components/shared/navigation/securePage.ts';
import { DateTime } from 'luxon';
import { FilterRef } from '@features/statistics/types.ts';

interface GateDetailData {
    gateName: string;

    [key: string]: string | number;
}

interface SeriesData {
    name: string;
    data: Array<{ x: string; y: number }>;
}

interface FilterArgs {
    shopId: string;
    start: string;
    end: string;
}

const GatesDetails: FCSecurePage = () => {
    const { t } = useTranslation();
    const { notifyErrorResponse } = useNotifications();
    const [dataLoading, setDataLoading] = useState(false);
    const [series, setSeries] = useState<SeriesData[]>([]);
    const [maxValue, setMaxValue] = useState(5);
    const [lastUpdate, setLastUpdate] = useState<DateTime | undefined>(undefined);
    const filterRef = useRef<FilterRef>(null);

    const loadData = async (args: FilterArgs) => {
        setDataLoading(true);
        const url = `statistics/wanzlGates/details/${args.shopId}/${args.start}/${args.end}`;

        try {
            const response = await apiClient.get<GateDetailData[]>(url);
            const data = response.data;
            setLastUpdate(DateTime.now());

            if (!Array.isArray(data)) {
                console.error('Expected array but got:', data);
                setSeries([]);
                setMaxValue(5);
                return;
            }

            const newSeries: SeriesData[] = [];
            let max = 0;

            data.forEach((element) => {
                let elMax = 0;
                Object.entries(element)
                    .filter(([, value]) => typeof value === 'number')
                    .forEach(([key, value]) => {
                        elMax += value as number;
                        const seriesName = t(key);
                        const seriesIndex = newSeries.findIndex((x) => x.name === seriesName);
                        if (seriesIndex > -1) {
                            newSeries[seriesIndex].data.push({
                                x: element.gateName,
                                y: value as number
                            });
                        } else {
                            const count = newSeries.push({ name: seriesName, data: [] });
                            newSeries[count - 1].data.push({
                                x: element.gateName,
                                y: value as number
                            });
                        }
                    });
                max = elMax > max ? elMax : max;
            });

            setMaxValue(max > 0 ? max : 5);
            setSeries(newSeries);
        } catch (error) {
            notifyErrorResponse(error);
            setSeries([]);
            setMaxValue(5);
        } finally {
            setDataLoading(false);
        }
    };

    const handleReload = useCallback(async () => {
        filterRef.current?.refresh();
    }, []);

    useRegisterRefresh(handleReload);

    return (
        <div>
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-view-carousel" />}
                title={`${t('gates')} ${t('details')}`}
                iconSize="20px"
                menus={getMenuData()}
                currentRoute={{ path: '/statistics/gates/details' }}
                lastUpdate={lastUpdate}
                t={t}
            />
            <div className="container grid-list-xl container--fluid no-padding-top">
                <FilterShopDateRange ref={filterRef} onLoadData={loadData} />
                <div className="row">
                    <AppSectionLoader status={dataLoading} />
                    <AppCard
                        heading={`${t('gates')} ${t('details')}`}
                        icon="view_carousel"
                        colClasses="col-spacing"
                        customClasses="no-padding-bottom"
                        fullBlock={false}
                        footer={false}
                    >
                        <WcnApexchart
                            type="bar"
                            series={series}
                            height={550}
                            chartId="gatesDetailsChart"
                            stacked={true}
                            dataLabelsEnabled={true}
                            xAxisType="category"
                            yAxisMax={maxValue}
                            yAxisMin={0}
                        />
                    </AppCard>
                </div>
            </div>
        </div>
    );
};

export default GatesDetails;
